import React, { useState, useEffect, useRef, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   COGNARA — THE REASONING STRATA
   Vertical reasoning descent: Question → 7 strata → Evidence → Answer
   Pure SVG + CSS keyframes + React state
   ═══════════════════════════════════════════════════════════════════════ */

/* ────────── CONTENT POOLS ────────── */
const QA_PAIRS = [
  {
    q: "Is our force majeure claim defensible under FIDIC Red Book 2017?",
    a: "Defensible if Clause 18.1 four-test is met and Particular Conditions 8.4 is unamended. Check rainfall vs IMD baseline and critical-path impact.",
    meta: "7 strata traversed · 12 sources cited · confidence: HIGH",
  },
  {
    q: "Code for centrifugal compressor at 50 MMSCFD sour gas service?",
    a: "API 617 9th Ed primary; NACE MR0175 materials; API 614 lube/seal; API 670 vibration; pulsation study per API 618.",
    meta: "7 strata traversed · 18 standards referenced · confidence: HIGH",
  },
  {
    q: "Concurrent delay: 47-day owner + 31-day contractor. EOT approach?",
    a: "Malmaison approach (Indian precedent) — full 47 days EOT granted. Prolongation cost only for net owner-attributable ~16–20 days.",
    meta: "7 strata traversed · 9 precedent cases · confidence: HIGH",
  },
  {
    q: "LD cap at 10% — enforceable under Indian contract law?",
    a: "Enforceable per Section 74 ICA + ONGC v. Saw Pipes. Remove 'subject to actual loss' qualifier per Kailash Nath (2015).",
    meta: "7 strata traversed · 3 SC precedents · confidence: HIGH",
  },
  {
    q: "Near-miss crane load-drop — incident report classification?",
    a: "Class II Serious Near-Miss per OISD-GDN-192; IOGP Life-Saving Rule #3 violation; PESO notification within 24h required.",
    meta: "7 strata traversed · 4 regulatory refs · confidence: HIGH",
  },
];

const EVIDENCE_POOL = [
  /* Stratum 1 — Contracts */
  [
    { type: "FIDIC CLAUSE", ref: "§ 8.4 / Red Book 2017" },
    { type: "FIDIC CLAUSE", ref: "§ 20.2 / Time Barring" },
    { type: "FIDIC CLAUSE", ref: "§ 18.1 / Exceptional Events" },
    { type: "NEC CLAUSE",   ref: "NEC4 ECC / Option B" },
    { type: "INDIAN ACT",   ref: "ICA 1872 § 74" },
  ],
  /* 2 — Codes & Standards */
  [
    { type: "API STANDARD", ref: "API 617 9th Ed" },
    { type: "ASME CODE",    ref: "B31.3 Ch II-304" },
    { type: "IS CODE",      ref: "IS 10431 Clause 6" },
    { type: "IEC STANDARD", ref: "IEC 61511 Part 1" },
    { type: "ISO STANDARD", ref: "ISO 14617 §§ 3-5" },
  ],
  /* 3 — Regulatory */
  [
    { type: "REGULATION",   ref: "BOCW Act § 4" },
    { type: "REGULATION",   ref: "CPCB Norms / 2019" },
    { type: "REGULATION",   ref: "PESO Rules 2023" },
    { type: "REGULATION",   ref: "DSEAR / UK 2002" },
  ],
  /* 4 — Project Management */
  [
    { type: "METHODOLOGY",  ref: "AWP / CII 272-2" },
    { type: "METHODOLOGY",  ref: "Last Planner / LCI" },
    { type: "FRAMEWORK",    ref: "CII RP-272 §§ 4-8" },
    { type: "FRAMEWORK",    ref: "PMBOK 7th / Perf" },
  ],
  /* 5 — Commercial */
  [
    { type: "COST REF",     ref: "AACE RP 17R-97" },
    { type: "COST REF",     ref: "Escalation Q3 2024" },
    { type: "EVM STANDARD", ref: "ANSI/EIA-748" },
    { type: "BENCHMARK",    ref: "RS Means / FY25" },
  ],
  /* 6 — Post-mortems */
  [
    { type: "PRECEDENT",    ref: "Jamnagar-III / 2019" },
    { type: "PRECEDENT",    ref: "Metro-VII Claim" },
    { type: "PATTERN",      ref: "Similar vendor lag" },
    { type: "CASE STUDY",   ref: "CAG Report 14/2022" },
  ],
  /* 7 — India-specific */
  [
    { type: "JURISDICTION", ref: "Gujarat / Land Use" },
    { type: "PRECEDENT",    ref: "ONGC v. Saw Pipes" },
    { type: "STATE NORMS",  ref: "Maharashtra SPCB" },
    { type: "BIS CODE",     ref: "IS 875-III / Wind" },
  ],
];

/* ────────── STRATA LAYOUT ────────── */
const STRATA = [
  { n: "01", name: "CONTRACTS",               sub: "FIDIC · NEC · JCT · ICA",        tokens: "680M", h: 54, pattern: "lines" },
  { n: "02", name: "CODES & STANDARDS",       sub: "API · ASME · IS · IEC · ISO",    tokens: "520M", h: 48, pattern: "hex" },
  { n: "03", name: "REGULATORY",              sub: "BOCW · CPCB · PESO · OSHA",       tokens: "340M", h: 44, pattern: "ticks" },
  { n: "04", name: "PROJECT MANAGEMENT",      sub: "PMBOK · PRINCE2 · CII · AACE",    tokens: "280M", h: 40, pattern: "bricks" },
  { n: "05", name: "COMMERCIAL & COST",       sub: "RS Means · Spon's · AACE RP",     tokens: "220M", h: 36, pattern: "digits" },
  { n: "06", name: "POST-MORTEMS",            sub: "10k+ forensics · IPA · CAG",      tokens: "220M", h: 36, pattern: "dots" },
  { n: "07", name: "INDIA-SPECIFIC",          sub: "PSU · IIT · Hindi bilingual",     tokens: "180M", h: 32, pattern: "hatch" },
];

const BAND_TOP = 100;   // Band B starts
const PROBE_X = 258;    // 38% of 640 = 243; bumped a bit for visual centering within strata pattern area
const EV_X = 486;       // Evidence trail column left edge
const TOTAL_CYCLE = 14; // seconds per question→answer cycle

/* compute per-stratum Y bounds */
const STRATUM_Y = (() => {
  let y = BAND_TOP;
  return STRATA.map((s) => {
    const top = y + 4;
    const bottom = y + 4 + s.h;
    y = bottom + 2; // 2px gap between strata
    return { top, bottom, mid: (top + bottom) / 2 };
  });
})();
const STRATA_END = STRATUM_Y[STRATUM_Y.length - 1].bottom;

/* ═══════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
export default function CognaraReasoningStrata() {
  const [cycle, setCycle] = useState(0);        // increments every TOTAL_CYCLE seconds
  const [phase, setPhase] = useState("descent"); // "descent" | "answer" | "fade"
  const [visited, setVisited] = useState([]);   // array of stratum indices already visited this cycle
  const [evidence, setEvidence] = useState([]); // [{idx, tok, critical}]
  const [answerProgress, setAnswerProgress] = useState(0); // 0..1
  const [reduceMotion, setReduceMotion] = useState(false);
  const [criticalStratum, setCriticalStratum] = useState(-1);
  const cycleStartRef = useRef(Date.now());

  /* reduced motion */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const fn = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const activePair = QA_PAIRS[cycle % QA_PAIRS.length];

  /* cycle: descent (9s) → answer stream (~3s) → fade (2s) → next */
  useEffect(() => {
    if (reduceMotion) return;
    cycleStartRef.current = Date.now();
    setVisited([]);
    setEvidence([]);
    setAnswerProgress(0);
    setPhase("descent");
    // occasional critical stratum (every ~18-22s ≈ every 2nd cycle)
    setCriticalStratum(cycle % 2 === 1 ? Math.floor(Math.random() * 7) : -1);

    /* schedule probe hitting each stratum (descent 9s over 7 strata) */
    const timers = [];
    STRATA.forEach((_, i) => {
      const ratio = (STRATUM_Y[i].mid - BAND_TOP) / (STRATA_END - BAND_TOP);
      timers.push(
        setTimeout(() => {
          setVisited((v) => [...v, i]);
          const pool = EVIDENCE_POOL[i];
          const tok = pool[Math.floor(Math.random() * pool.length)];
          setEvidence((ev) => [...ev, { idx: i, tok, critical: criticalStratum === i }]);
        }, ratio * 9000 + 300)
      );
    });
    /* answer stream start after descent completes (9.3s) */
    timers.push(setTimeout(() => setPhase("answer"), 9300));
    /* answer text fills over ~3s */
    const streamSteps = 30;
    for (let s = 1; s <= streamSteps; s++) {
      timers.push(setTimeout(() => setAnswerProgress(s / streamSteps), 9300 + s * 100));
    }
    /* fade & loop */
    timers.push(setTimeout(() => setPhase("fade"), 12700));
    timers.push(setTimeout(() => setCycle((c) => c + 1), TOTAL_CYCLE * 1000));
    return () => timers.forEach(clearTimeout);
  }, [cycle, reduceMotion]); // eslint-disable-line react-hooks/exhaustive-deps

  /* visible answer text based on streaming progress */
  const visibleAnswer = useMemo(() => {
    const full = activePair.a;
    return full.slice(0, Math.floor(full.length * answerProgress));
  }, [activePair, answerProgress]);

  return (
    <div className="srs-wrap" data-testid="srs-canvas" role="img"
         aria-label="Animated illustration: Cognara reasons through seven layers of domain knowledge. A question enters at the top, a query probe descends through Contracts, Codes and Standards, Regulatory Frameworks, Project Management, Commercial and Cost, Post-mortems, and India-specific corpora, extracting specific evidence citations at each layer. The accumulated evidence assembles into a synthesized answer at the bottom.">
      <svg viewBox="0 0 640 560" className={`srs-svg ${reduceMotion ? "srs-reduced" : ""}`}
           preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          {/* stratum patterns */}
          <pattern id="srs-pat-lines" width="6" height="4" patternUnits="userSpaceOnUse">
            <line x1="0" y1="2" x2="6" y2="2" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
          <pattern id="srs-pat-hex" width="12" height="10" patternUnits="userSpaceOnUse">
            <polygon points="3,0 9,0 12,5 9,10 3,10 0,5" fill="none" stroke="currentColor" strokeWidth="0.4" />
          </pattern>
          <pattern id="srs-pat-ticks" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="2" y1="1" x2="2" y2="7" stroke="currentColor" strokeWidth="0.5" />
            <line x1="6" y1="1" x2="6" y2="7" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
          <pattern id="srs-pat-bricks" width="12" height="6" patternUnits="userSpaceOnUse">
            <rect x="0.5" y="0.5" width="5" height="2" fill="none" stroke="currentColor" strokeWidth="0.4" />
            <rect x="6.5" y="3.5" width="5" height="2" fill="none" stroke="currentColor" strokeWidth="0.4" />
          </pattern>
          <pattern id="srs-pat-digits" width="14" height="6" patternUnits="userSpaceOnUse">
            <text x="0" y="5" fontFamily="JetBrains Mono,monospace" fontSize="5" fill="currentColor">₹·</text>
          </pattern>
          <pattern id="srs-pat-dots" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.8" fill="currentColor" />
            <circle cx="6" cy="5" r="0.6" fill="currentColor" />
          </pattern>
          <pattern id="srs-pat-hatch" width="6" height="6" patternUnits="userSpaceOnUse">
            <line x1="0" y1="6" x2="6" y2="0" stroke="currentColor" strokeWidth="0.4" />
          </pattern>
          {/* probe glow */}
          <radialGradient id="srs-probe-glow">
            <stop offset="0%" stopColor="#5790C9" stopOpacity="1" />
            <stop offset="100%" stopColor="#5790C9" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ═══════ TOP OVERLAY STRIP ═══════ */}
        <rect x="0" y="0" width="640" height="24" className="srs-topbar-bg" />
        <text x="8" y="16" className="srs-top-left">COGNARA · MULTI-AGENT</text>
        <text x="320" y="16" textAnchor="middle" className="srs-top-mid">2.4B TOKENS · 7 CORPORA · 9 AGENTS</text>
        <text x="632" y="16" textAnchor="end" className="srs-top-right">INDIA-FIRST</text>
        <line x1="0" y1="25" x2="640" y2="25" className="srs-hair" />

        {/* ═══════ BAND A — QUESTION ═══════ */}
        <g key={`q-${cycle}`} className="srs-band-a">
          <rect x="100" y="44" width="440" height="42" rx="10" className="srs-q-pill" />
          {/* user icon */}
          <circle cx="118" cy="65" r="7" className="srs-q-icon-bg" />
          <circle cx="118" cy="62" r="2.2" className="srs-q-icon-head" />
          <path d="M112,69 Q118,66 124,69" fill="none" className="srs-q-icon-body" strokeWidth="1" />
          {/* question text */}
          <foreignObject x="132" y="50" width="380" height="30">
            <div xmlns="http://www.w3.org/1999/xhtml" className="srs-q-text">{activePair.q}</div>
          </foreignObject>
          {/* blinking cursor */}
          <rect x="526" y="56" width="1.4" height="18" className="srs-q-cursor" />
          {/* down arrow */}
          <polygon points={`${PROBE_X - 5},90 ${PROBE_X + 5},90 ${PROBE_X},95`} className="srs-q-arrow" />
        </g>

        {/* ═══════ BAND B — STRATA ═══════ */}
        {STRATA.map((s, i) => {
          const y = STRATUM_Y[i];
          const isActive = visited.includes(i);
          const isCritical = isActive && criticalStratum === i;
          return (
            <g key={s.n} className={`srs-stratum ${isActive ? "srs-stratum-active" : ""} ${isCritical ? "srs-stratum-critical" : ""}`}>
              {/* background fill area */}
              <rect x="120" y={y.top} width="360" height={s.h} className="srs-stratum-bg" />
              {/* pattern fill (colored via currentColor) */}
              <rect x="120" y={y.top} width="360" height={s.h} fill={`url(#srs-pat-${s.pattern})`} className="srs-stratum-pattern" />
              {/* dividers */}
              <line x1="120" y1={y.top} x2="480" y2={y.top} className="srs-stratum-div" />
              {/* left-edge labels */}
              <text x="8" y={y.mid - 6} className="srs-stratum-num">{s.n}</text>
              <text x="8" y={y.mid + 4} className="srs-stratum-name">{s.name}</text>
              <text x="8" y={y.mid + 15} className="srs-stratum-tokens">{s.tokens}</text>
              {/* right-edge corpus handle */}
              <rect x="480" y={y.top + 2} width="3" height={s.h - 4} className="srs-stratum-handle" />
              <rect x="479" y={y.top} width="5" height="4" className="srs-stratum-handle-top" />
            </g>
          );
        })}

        {/* ═══════ QUERY PROBE ═══════ */}
        {!reduceMotion && phase === "descent" && (
          <g className="srs-probe-wrap">
            <ProbeTrail />
          </g>
        )}
        {reduceMotion && (
          <line x1={PROBE_X} y1={BAND_TOP} x2={PROBE_X} y2={STRATA_END} className="srs-probe-static" />
        )}

        {/* ═══════ EVIDENCE EXTRACTION BEAMS (flash when each stratum hit) ═══════ */}
        {evidence.map((e, i) => {
          const y = STRATUM_Y[e.idx].mid;
          return (
            <line key={`beam-${cycle}-${e.idx}`}
                  x1={PROBE_X + 4} y1={y}
                  x2={EV_X - 4} y2={y}
                  className={`srs-beam ${e.critical ? "srs-beam-critical" : ""}`}
                  style={{ animationDelay: "0ms" }} />
          );
        })}

        {/* ═══════ BAND C — EVIDENCE TRAIL ═══════ */}
        <text x={EV_X} y={BAND_TOP - 6} className="srs-evid-header">EVIDENCE TRAIL</text>
        {evidence.map((e) => {
          const y = STRATUM_Y[e.idx].mid;
          return (
            <g key={`ev-${cycle}-${e.idx}`} transform={`translate(${EV_X}, ${y - 14})`}>
              <g className={`srs-ev-token ${e.critical ? "srs-ev-token-critical" : ""} ${phase === "fade" ? "srs-ev-fade" : ""}`}>
                <rect x="0" y="0" width="140" height="28" rx="5" className="srs-ev-bg" />
                <rect x="0" y="0" width="2.5" height="28" className="srs-ev-stripe" />
                <text x="8" y="11" className="srs-ev-type">{e.tok.type}</text>
                <text x="8" y="22" className="srs-ev-ref">{e.tok.ref}</text>
                {/* AI spark */}
                <g className="srs-ev-spark" transform="translate(128, 6)">
                  <path d="M0,-4 L-2.5,0 L0,0 L-1,4 L3,-1 L1,-1 Z" className="srs-ev-spark-path" />
                </g>
              </g>
            </g>
          );
        })}

        {/* ═══════ BAND D — ANSWER CARD ═══════ */}
        {(phase === "answer" || phase === "fade") && (
          <g transform="translate(60, 465)">
            <g className={`srs-answer ${phase === "fade" ? "srs-answer-fade" : ""}`}>
              <rect x="0" y="0" width="520" height="76" rx="8" className="srs-ans-bg" />
              <rect x="0" y="0" width="520" height="3" rx="1.5" className="srs-ans-border" />
              {/* orange spark */}
              <g transform="translate(14, 22)">
                <path d="M0,-6 L-4,0 L0,0 L-2,6 L5,-1 L2,-1 Z" className="srs-ev-spark-path" />
              </g>
              <text x="28" y="20" className="srs-ans-brand">COGNARA</text>
              {/* streaming answer text via foreignObject for wrapping */}
              <foreignObject x="14" y="28" width="500" height="34">
                <div xmlns="http://www.w3.org/1999/xhtml" className="srs-ans-text">
                  {visibleAnswer}
                  {answerProgress < 1 && <span className="srs-ans-caret">▌</span>}
                </div>
              </foreignObject>
              {answerProgress >= 1 && (
                <text x="14" y="69" className="srs-ans-meta">⚡ {activePair.meta}</text>
              )}
            </g>
          </g>
        )}

        {/* ═══════ BOTTOM LABELS ═══════ */}
        <line x1="0" y1="540" x2="640" y2="540" className="srs-hair" />
        <text x="8"   y="554" className="srs-bot-label">QUERY</text>
        <text x="320" y="554" textAnchor="middle" className="srs-bot-label srs-bot-label-accent">REASON</text>
        <text x="632" y="554" textAnchor="end" className="srs-bot-label srs-bot-label-accent">CITE</text>
      </svg>

      <span className="sr-only">
        Cognara reasons through seven layers of domain knowledge: Contracts, Codes & Standards,
        Regulatory, Project Management, Commercial & Cost, Post-mortems, and India-specific. A
        question enters at the top, a query probe descends through all seven strata extracting
        cited evidence, and a synthesized answer assembles at the bottom.
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   PROBE TRAIL — descending animated element (pure CSS animation)
   ═══════════════════════════════════════════════════════════════════════ */
function ProbeTrail() {
  return (
    <g className="srs-probe-anim">
      {/* tail */}
      {[60, 45, 32, 22, 14, 8].map((dy, i) => (
        <circle key={i} cx={PROBE_X} cy={BAND_TOP - dy} r={[1.2, 1.6, 2, 2.5, 3.2, 4.5][i]}
                className="srs-probe-tail" style={{ opacity: [0.12, 0.2, 0.3, 0.4, 0.55, 0.7][i] }} />
      ))}
      {/* core */}
      <circle cx={PROBE_X} cy={BAND_TOP} r="7" className="srs-probe-halo" />
      <circle cx={PROBE_X} cy={BAND_TOP} r="4" className="srs-probe-core" />
    </g>
  );
}
